import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { Button, Tooltip, useToast } from "@chakra-ui/react";
import { Breadcrumb, ScoreMeter } from "../../Components/Atoms";
import { RideMap } from "../../Components/Molecules";

import PATHS from "../../Routes/paths";
import { filterInvalidSpeedPings, getStatLabel } from "../../Library/helpers";
import { computeTLStats, computeWirelessStats,  sphericalCosinus } from "../../Library/geodist";

import "./styles.css";
import { getDriversInfo, getDriverOrVehicleStats, getAlerts, getRoutes} from "../../APIs";
import { DownloadIcon, InfoIcon } from "../../Assets/images/svgs";
import Loader from "../Loader";
import { getDateEnd, getDateStart, getLocalisedDate, getLocalisedTime, stampToDate } from "../../Library/datetime";
import { StoreContext } from "../../Library/contexts";
import { GEOFENCE_TRIGGER_TYPE, DEVICE_HWTYPS } from "../../Library/constants";
import { handleReportDownload } from "../../Library/jsonUtils";
const DEVIATION_THRESHOLD = 0.4; // in kms;

const DAILY_STAT_TYPES = [
  {  title: "Ride Date",       format: "date",       key: "ts"   },
  {  title: "Ride Start Time", format: "time",       key: "sdt", varying: true  },
  {  title: "Ride End Time",   format: "time",       key: "edt", varying: true  },
  {  title: "Total Distance",  format: "distance",   key: "dst", varying: true  },
  {  title: "Average Speed",   format: "speed"   ,   key: "avs", varying: true  },
  {  title: "Top Speed",       format: "speed"   ,   key: "tps", varying: true  },
  {  title: "Total Duration",  format: "duration",   key: "dur", varying: true  },
];

const ROUTE_STAT_TYPES = [
  { title: "Expect Route Dist.", format: "distance", key: "routeDst", route: true },
  { title: "Actual Route Dist.", format: "distance", key: "dst", varying: true },
  { title: "Deviated Dist.", format: "distance", key: "deviated", route: true, deviation: true },
  { title: "Total Duration", format: "duration", key: "dur", varying: true },
  { title: "Avg Speed", format: "speed", key: "avs", varying: true },
  { title: "Top Speed", format: "speed", key: "tps", varying: true },
  { title: "Ride Start Time", format: "time", key: "sdt", varying: true  },
  { title: "Ride End Time", format: "time", key: "edt", varying: true  },
];

const RIDING_EVENTS = [
  { key: "HARSH_ACCELERATION", label: "Harsh Acceleration" },
  { key: "SHARP_TURN", label: "Sharp Turn" },
  { key: "SUDDEN_BRAKE", label: "Sudden Breaking" },
  { key: "OVERSPEED", label: "Overspeed Limit" },
];

const RideDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const { userVehicles, allGeofences, setAllGeofences, allVehicleRoutes, setAllVehicleRoutes } = useContext(StoreContext);
  const [ ride, setRide ] = useState((location.state && location.state.ride) || {});
  const [ vid, setVid] = useState(ride.vid || searchParams.get("vid") || null);
  const isWireless = useRef(false);
  const [ vehicleDetails, setVehicleDetails] = useState({});
  const [ showExistingGeofences, setShowExistingGeofences ] = useState(false);
  const [ geofencesToShow, setGeofencesToShow ] = useState(false);
  const [ selectedRoute, setSelectedRoute ] = useState(null);
  const [ driverData, setDriverData ] = useState(null);

  const reg = searchParams.get("reg");
  const ts = searchParams.get("ts");
  const imei = searchParams.get("imei");
  const isRoute = searchParams.get("route");
  const liveTracking = useRef(searchParams.get("liveTracking") === "true");
  const isUser = useRef();
  const isSellerOrDealer = useRef(false);
  const isRideDataFetched = useRef(false);
  const Toast = useToast();
  const splitPathSegments = useRef([]);

  const paths = useMemo(() => {
    const state = { reg, vid };

    const navDetails = (e) => {
      e.preventDefault();
      navigate([PATHS.LEASED_VEHICLES, PATHS.VEHICLE_STATS].join("/") + "?idx=" + vid, { state });
    };

    const navRides = (e) => {
      e.preventDefault();
      navigate([PATHS.LEASED_VEHICLES, PATHS.PAST_RIDES].join("/") + "/" + vid, { state });
    };

    return [
      { idx: 1, name: "Lease", link: PATHS.LEASE },
      { idx: 2, name: "Vehicles", link: PATHS.LEASED_VEHICLES },
      { idx: 3, name: (reg || vid), cb: navDetails },
      { idx: 3, name: "Past Rides", cb: navRides },
      { idx: 3, name: "Ride Details", isCurrentPage: true },
    ];
  }, [ navigate, reg, vid ]);

  const userPaths = useMemo(() => {
    const state = { reg, vid };
    const navRides = (e) => {
      e.preventDefault();
      navigate(`${PATHS.LIVE_TRACKING}/${PATHS.PAST_RIDES}/${vid}${location.search || ""}`, { state });
    };

    if (searchParams.get("route")) {
      const source = searchParams.get("source");
      let registration = searchParams.get("reg");
      let vehicleId = searchParams.get("vid");
      let routeId = searchParams.get("route");
      let driverId = searchParams.get("driverId");
      let dateStr = searchParams.get("date");

      if (source === "route_history") {
          const routeName = allVehicleRoutes?.find(r => String(r.idx) === String(routeId))?.nm || "Route Trips";
          return [
              { idx: 1, name: "Route History", link: PATHS.ROUTE_HISTORY },
              { idx: 2, name: dateStr || "Date", link: dateStr ? `${PATHS.ROUTE_HISTORY}?date=${dateStr}` : -1 },
              { idx: 3, name: routeName, cb: (e) => { e.preventDefault(); navigate(-1); } },
              { idx: 4, name: `${getLocalisedTime(ride.startTs)} - ${getLocalisedTime(ride.endTs)}`, isCurrentPage: true },
          ];
      }

      if (source === "driver_trips") {
          // New Driver Flow
          const driverName = driverData?.dNm || "Driver Details";
          const routeName = allVehicleRoutes?.find(r => String(r.idx) === String(routeId))?.nm || "Route Trips";

          return [
              { idx: 1, name: "Driver Management", link: PATHS.DRIVER_MANAGEMENT },
              { idx: 2, name: driverName, link: driverId ? `${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_SUMMARY}?idx=${driverId}` : -1 },
              { idx: 3, name: routeName, cb: (e) => { e.preventDefault(); navigate(-1); } },
              { idx: 4, name: `${getLocalisedTime(ride.startTs)} - ${getLocalisedTime(ride.endTs)}`, isCurrentPage: true },
          ];
      }

      return [
        { idx: 1, name: "Summary", link: PATHS.VIEW_ROUTES },
        { idx: 2, name: `${registration} Routes`, link: `${PATHS.VIEW_ROUTES}${PATHS.ROUTES_SUMMARY}?reg=${registration}&idx=${vehicleId}` },
        { idx: 3, name: "Day wise split", link: `${PATHS.VIEW_ROUTES}/${PATHS.PAST_RIDES}/${vehicleId}?route=${routeId}&reg=${registration}` },
        { idx: 4, name: `${stampToDate(+searchParams.get("ts"))} Trips`, link: `${PATHS.VIEW_ROUTES}/${PATHS.PAST_RIDES}/${vehicleId}?route=${routeId}&reg=${registration}&trips=true&ts=${searchParams.get("ts")}` },
        { idx: 5, name: `${getLocalisedTime(ride.startTs)} - ${getLocalisedTime(ride.endTs)}` },
      ];
    }

    return [
      { idx: 1, name: "Live Tracking", link: PATHS.LIVE_TRACKING },
      { idx: 2, name: liveTracking.current ? "Device Details" : reg, link: liveTracking.current ? `${PATHS.LIVE_TRACKING}/${PATHS.SPECIFIC_DEVICE}?idx=${vid}&imei=${imei}&lt=true&showButtons=true&liveTracking=true` : `${PATHS.LIVE_TRACKING}/${PATHS.VEHICLE_STATS}?idx=${vid}&lt=true` },
      { idx: 3, name: "Past Rides", cb: navRides },
      { idx: 4, name: "Ride Details", isCurrentPage: true },
    ];
  }, [location.search, navigate, reg, vid, driverData]);

  const [ isLoading, setIsLoading ] = useState(true);
  const [ rideData, setRideData ] = useState({});
  const originalData = useRef([]);
  const pingData = useRef([]);
  const pathData = useRef([]);
  const pitstopData = useRef([]);

  const [ mapCenter, setMapCenter ] = useState({});

  const handleUserVehicleDetails = (vehs) => {
    if (vehs && vehs.length) {
      let vehicle = vehs.find(vehicle => vehicle.reg === reg);
      if (vehicle) {
        setVehicleDetails(vehicle);
        setVid(vehicle.idx);
        if (vehicle.hwtyp === DEVICE_HWTYPS["Wireless"] || vehicle.hwtyp === DEVICE_HWTYPS["Lite"]) isWireless.current = true;
      }
      else if (!searchParams.get("source")) {
        navigate(PATHS.LEASED_VEHICLES);
      }
    }
  }

  useEffect(() => {
    if (!vid) return;
    if (!userVehicles || !userVehicles.length) return;

    handleUserVehicleDetails(userVehicles);

    let user = window.localStorage.getItem("user");
    let sellerOrDealer = window.localStorage.getItem("des");
    try {
      user = JSON.parse(user);
      sellerOrDealer = JSON.parse(sellerOrDealer);
    } catch (e) {
      console.error(e);
    }

    if (user) isUser.current = user;
    if (sellerOrDealer) isSellerOrDealer.current = !!sellerOrDealer;

    const fetchRideData = (user) => {
      if (isRideDataFetched.current || !Number(vid)) return;
      if (!ride || !ride.sdt || !ride.edt) return;

      let data = { vid: Number(vid), tsFr: Math.floor(new Date(ride.sdt * 1000).setHours(0,0,0,0) / 1000), tsTo: Math.floor(ride.edt), orphan: false };
      if (isUser.current) {
        data.isWeb = true;
        data.fromTs = data.tsFr;
        data.toTs = data.tsTo;
      }

      let socket = user ? global.UWS : global.WS;

      if (socket) {
          socket.sendRequest({
            action: !isUser.current ? (isWireless.current ? "getWirelessPings" : "getTLLocEvents") : "getLocEvents",
            data: data,
          });
      }
    };

    const fetchWSData = () => fetchRideData(false);
    const fetchUWSData = () => fetchRideData(true);

    global.EE.addEventListener(global.EE.EVENT_TYPES.UWS_CONNECTED, fetchUWSData);
    global.EE.addEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, fetchWSData);

    if (global.WS && global.WS.wsConnected) {
      fetchRideData();
    } else if (global.UWS && global.UWS.wsConnected) fetchRideData(true);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.UWS_CONNECTED, fetchUWSData);
      global.EE.removeEventListener(global.EE.EVENT_TYPES.WS_CONNECTED, fetchWSData);
    }
  }, [ride, vid, userVehicles]);

  useEffect(() => {
    if (isRoute) {
      if (allVehicleRoutes && allVehicleRoutes.length) {
        let selRoute = allVehicleRoutes.find(route => route.idx === +isRoute);
        if (selRoute && selRoute.idx) setSelectedRoute(selRoute);
      } else {
        getRoutes((err, data) => {
          if (err) console.error(err);
          else {
            let activeRoutes = data.filter(route => !route.archived);
            let selRoute = activeRoutes.find(route => route.idx === +isRoute);
            if (selRoute && selRoute.idx) setSelectedRoute(selRoute);
            setAllVehicleRoutes(activeRoutes);
          }
        });
      }
    }
  }, [ isRoute, allVehicleRoutes ]);

  const onRideData = (ping) => {
    if (ping.ts !== 0) {
      if(ping.ignition || ping.spd > 5 || isWireless.current)  pingData.current.push(ping);
    } else {
      let deviatedSegments = ride.segments && ride.segments.length ? ride.segments.filter(seg => (seg.deviated > DEVIATION_THRESHOLD) || ((seg.to - seg.from) > 1)) : [];

      isRideDataFetched.current = true;
      pingData.current = filterInvalidSpeedPings([...pingData.current]);
      pingData.current.sort((a, b) => (a.ts - b.ts));
      if (!originalData.current.length) {
        originalData.current = [ ...pingData.current ];
      }

      let dailyRideFunc = computeTLStats;
      if(isWireless.current) dailyRideFunc = computeWirelessStats;
      let rideData = dailyRideFunc(pingData.current);

      // If !Wireless Device
      // pingData.filter((ping) => ping.ignition);

      let pitstops = [];    // Points where Rider Stopped for at least 20 minutes
      let prevPing = null;
      pingData.current.forEach((ping, i) => {
        if (!ping.lat || !ping.lng) return;

        // Filtering Out Consecutive Ignition OFF Pings to Reduce Fluctuation
        let lastPing = pingData.current[i - 1];
        if (!isUser.current) {
          if (lastPing && !lastPing.ignition && !ping.ignition) return;
          if (!lastPing && !ping.ignition) return;
        }

        if (prevPing && (ping.ts - prevPing.ts >= (20 * 60))) {
          // Check in Pitstop
          let matched = pitstops.find((pitstop) => {
            let distance = sphericalCosinus(pitstop.location.lat, pitstop.location.lng, ping.lat, ping.lng);
            return (distance < 100);
          });
          if (!matched) {
            pitstops.push({
              location: { lat: ping.lat, lng: ping.lng },
              entries: [{ from: prevPing, to: ping }],
            });
          } else {
            matched.entries.push({ from: prevPing, to: ping });
          }
        }

        pathData.current.push(ping);
        prevPing = ping;
      });

      pitstopData.current = pitstops;

      if (pathData.current.length > 2) {
        setMapCenter({
          lat: pathData.current[Math.round(pathData.current.length / 2)].lat,
          lng: pathData.current[Math.round(pathData.current.length / 2)].lng,
        });
      }

      global.gPingData = pingData;
      global.gRideData = rideData;
      global.gPitstops = pitstops;
      global.gPathData = pathData;
      if (location.state && location.state.ride && rideData) {
        setRideData({
          meta: rideData,
          stats: (searchParams.get("route") ? ROUTE_STAT_TYPES : DAILY_STAT_TYPES).map((stat, i) => {
            const computed = rideData[stat.key];
            const value = stat.varying ? (computed != null ? computed : ride[stat.key]) : (computed || ride[stat.key]);
            console.log('computed', computed, value);
            return {
              idx: i,
              title: stat.title,
              label: getStatLabel(value, stat.format, 1),
              deviation: stat.deviation,
            };
          }),
        });
      }
      setIsLoading(false);

      let deviatedIndexes = [];
      if (deviatedSegments.length) {
        deviatedSegments.forEach(segment => {
          let filteredPings = pathData.current.filter(p => p.ts >= segment.startTs && p.ts <= segment.endTs);
          const startIdx = pathData.current.findIndex(p => p.ts === filteredPings[0].ts);
          let endIdx = pathData.current.findIndex(p => p.ts === filteredPings[filteredPings.length - 1].ts);
          deviatedIndexes.push({ start: startIdx, end: endIdx });
        });
      }

      const path = pathData.current;

      let lastIndex = 0;

      if (deviatedIndexes.length) {
        deviatedIndexes.forEach(({ start, end }) => {
          if (start > lastIndex) {
            splitPathSegments.current.push({
              path: path.slice(lastIndex, start),
              color: '#217DBB',
            });
          }

          splitPathSegments.current.push({
            path: path.slice(start, end + 1),
            color: '#E25555',
          });

          lastIndex = end;
        });
      }

      if (lastIndex < path.length) {
        splitPathSegments.current.push({
          path: path.slice(lastIndex),
          color: '#217DBB',
        });
      }
    }
  };

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.ON_VEHICLE_DATA, onRideData);
    };
  }, []);

  useEffect(() => {
    // Fetch Driver Details on Page Load
    if (ride && ride.drId) {
      getDriversInfo(null, ride.drId, false, null, (_, driver) => {
        if (driver && driver[0] && driver[0].dNm) setDriverData(driver[0]);
      });
    }

    let range = { from: getDateStart(Number(ts)), to: getDateEnd(Number(ts)) };

    if (vid && (!ride || !ride.ts)) {
      getDriverOrVehicleStats(vid, null, range,  (err, res) => {
        if (err) {
          console.error("Error Fetching Ride Data", err);
        } else {
          if (res.dayWiseStats && res.dayWiseStats.length) {
            let ride = res.dayWiseStats.find(ride => ride.ts === Number(ts));
            if (ride) {
              setRide(ride);
              setRideData({
                meta: ride,
                stats: DAILY_STAT_TYPES.map((stat, i) => ({
                  idx: i,
                  title: stat.title,
                  label: getStatLabel(ride[stat.key] || 0, stat.format, 1),
                })),
              })
            }
          }
        }
      });
    }
  }, [ride, ts, vid]);

  const getDriver = () => {
    if (!driverData) return <span>{"-"}</span>
    return (
      <Link className="text-blue" to={`${PATHS.LEASE}/${PATHS.DRIVERS}/${PATHS.DRIVER_STATS}?idx=${ride.drId}`}>
        {driverData.dNm}
      </Link>
    );
  };

  if (!ts || !reg) return <Navigate to={PATHS.LEASED_VEHICLES} />;

  const getFilteredRideData = (from, to) => {
    if (from && to) {
      from = from / 1000;
      to = to / 1000;

      pingData.current = [];
      pathData.current = [];
      pitstopData.current = [];
      setIsLoading(true);

      pingData.current = originalData.current.filter(ping => {
        return (ping.ts >= from && ping.ts <= to);
      })

      onRideData({ ts: 0 });
    }
  }


  const getDistanceBreakdown = () => {
    if (rideData.meta?.dayDist) {
      let start = new Date(rideData.meta.sdt * 1000);
      let end = new Date(rideData.meta.edt * 1000);

      let startDate = start.toLocaleDateString("en-IN", { day: "2-digit", month: "long" });
      let endDate = end.toLocaleDateString("en-IN", { day: "2-digit", month: "long" });

      let startTime = start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });
      let endTime = end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

      return `This includes the distance between ${startTime} (${startDate}) to ${endTime} (${endDate}). The distance travelled on ${startDate} (upto 11:59pm) is ${(rideData.meta.dayDist / 1000).toFixed(2)}kms.`;
    }
  }

  const resetRideData = () => {
    pingData.current = originalData.current;
    pathData.current = [];
    pitstopData.current = [];

    onRideData({ ts: 0 });
  }

  const isSameDay = rideData.meta && (new Date(rideData.meta.sdt * 1000).getDate() === new Date(rideData.meta?.edt * 1000).getDate());

  const handleGeofences = (isChecked) => {
    if (isRoute) {
      setGeofencesToShow(selectedRoute.fences);
      setShowExistingGeofences(isChecked);
      return;
    }

    if (!allGeofences || !allGeofences.length) {
      getAlerts(GEOFENCE_TRIGGER_TYPE, (err, data) => {
        if(err) console.error("Error fetching geofence alerts", err);
        else {

          setAllGeofences(data);
          let geofenceForSelVehicle = data.filter(alert => alert.vehIdArr && alert.vehIdArr.includes(vid));
          if (selectedRoute && selectedRoute.idx) {
            let routeFences = selectedRoute.fences.map(fence => fence.idx);
            geofenceForSelVehicle = geofenceForSelVehicle.filter(fence => routeFences.includes(fence.idx));
          }
          setGeofencesToShow(geofenceForSelVehicle || []);
          if (!data.length) Toast({ isClosable: true, description: "You haven't added any geofences yet", position: "bottom", variant: "subtle", duration: 3000 })
        }
      });
    } else {
      setAllGeofences(allGeofences);
      let geofenceForSelVehicle = allGeofences.filter(alert => alert.vehIdArr && alert.vehIdArr.includes(vid));
      if (selectedRoute && selectedRoute.idx) {
        let routeFences = selectedRoute.fences.map(fence => fence.idx);
        geofenceForSelVehicle = geofenceForSelVehicle.filter(fence => routeFences.includes(fence.idx));
      }
      setGeofencesToShow(geofenceForSelVehicle || []);
    }

    setShowExistingGeofences(isChecked);
  }

  const handleRideReport = (ridedata) => {
    let dataToDownload = [];
    let totalDst = 0;
    ridedata.forEach((ping, idx) => {
      let prevPing = ridedata[idx - 1];
      let dst = 0;
      if (prevPing && prevPing.lat && prevPing.lng) dst = sphericalCosinus(ping.lat, ping.lng, prevPing.lat, prevPing.lng);
      if (dst) {
        dst = (dst / 1000);
        totalDst += dst;
      }
      dataToDownload.push({ idx: idx + 1, date: getLocalisedDate(ping.ts * 1000, true), time: getLocalisedTime(ping.ts * 1000, true, null, true), speed: ping.spd, distance: dst, totalDistance: totalDst, lat: ping.lat, lng: ping.lng });
    });

    if (dataToDownload && dataToDownload.length) handleReportDownload(dataToDownload);
  }

  return (
    <>
      {!isLoading ? <div className="panel-main past-rides ride-details">
        <header>
          <Breadcrumb paths={(isUser.current || isSellerOrDealer.current) ? userPaths : paths} />
        </header>

        {isLoading ? (
          <div />
        ) : ( (rideData && rideData.stats) ?
            <>
              <div className="ride-wrapper">
                <article className="card widget-group">
                <div className="ride-stats-head">
                  <h3>Ride Stats</h3>
                  {isUser.current ? <hr /> : null}
                </div>
                <Tooltip label={"Download ride report"} hasArrow placement="left">
                  <Button className="download-ride-report-btn" onClick={() => handleRideReport(pathData.current)}>
                    <DownloadIcon />
                  </Button>
                </Tooltip>

                <div className="widget-group keyvals">
                    {[...rideData.stats].map((stat) => (
                      <main className={`widget-main ${searchParams.get("route") ? "route-stat-container" : ""}`} key={stat.idx}>
                        <header>{stat.title}</header>
                        <article className={stat.deviation ? "highlight-deviation" : ""}>
                          {stat.label}
                          {stat.title === "Total Distance" ? (
                            (!isSameDay && <Tooltip className="distance-tooltip" closeOnScroll={false} hasArrow label={getDistanceBreakdown()} bg="#fff" color="black" placement="top">
                              <InfoIcon className="distance-info" />
                            </Tooltip>)
                          ) : ""}
                        </article>
                      </main>
                    ))}
                    {(!isUser.current && !isSellerOrDealer.current) ? <main className="widget-main">
                      <header>Driver Name</header>
                      <article>{getDriver()}</article>
                    </main> : null}
                  </div>
                </article>
                {ride.rideScoreCounts ?
                  <article className="card widget-group">
                    <div className="ride-stats-head">
                      <h3>Riding Score</h3>
                      <hr />
                    </div>
                  <div className="ride-score-container">
                    <div className="meter-container">
                      <ScoreMeter score={ride?.rideScore} showAboutScore={false}/>
                    </div>
                    <div className="riding-events">
                      {
                        RIDING_EVENTS.map((event, idx)=>(
                          <div className="riding-score-val" key={idx}>
                            <span className="val">{ride.rideScoreCounts ? ride.rideScoreCounts[event.key] : 0}</span>
                            <span className="abt">{event.label}</span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  </article> : null}
              </div>
              {pathData.current && pathData.current.length ?
                <div className="ride-map-container">
                  <RideMap isRoute={isRoute} hideGfBtn={isSellerOrDealer.current} geofencesToPlot={geofencesToShow} showExistingGfsToggle={true} showExistingGeofences={showExistingGeofences} setShowExistingGeofences={handleGeofences} vehicleDetails={vehicleDetails} handleRideFilterReset={resetRideData} handleRideFilter={getFilteredRideData} splitPathSegments={splitPathSegments.current} pathTrail={pathData.current} pitstops={pitstopData.current} mapCenter={mapCenter} />
                </div>
              : null}
            </>
            : <span className="no-data-msg">No data available !!</span>
        )}
      </div> : <Loader hide={true} />}
    </>
  );
};


export default RideDetails;